
import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { BookOpen, GraduationCap, Presentation, Lightbulb, Users, Globe, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const PeteProfile = () => {
  const user = {
    name: "Pete Dumont",
    role: "Advisor - Construction and Capital Projects",
    email: "pdumont@prairiedogvp.com",
    solutionBounties: 8,
    domains: [
      "Curriculum Development",
      "Knowledge Transfer",
      "Academic Partnerships",
      "Educational Technology",
      "Learning Management Systems",
      "Corporate Training",
      "Skill Development",
      "Mentorship Programs",
      "Continuing Education"
    ],
    industries: [
      { name: "Education", icon: GraduationCap },
      { name: "Academia", icon: BookOpen },
      { name: "Consulting", icon: Presentation },
      { name: "Innovation", icon: Lightbulb },
      { name: "Human Resources", icon: Users },
      { name: "Global", icon: Globe }
    ],
    confirmedSolutions: 32,
    confirmedKnowledgeBases: 60,
    tokenBalance: 190,
    bio: "Pete Dumont is an Advisor for Construction and Capital Projects at iSPAI, bringing a wealth of experience in curriculum development, knowledge transfer, and academic partnerships. With a background in education and instructional design, Pete is passionate about creating engaging and effective learning experiences. He specializes in developing educational programs, designing learning management systems, and fostering corporate training initiatives. Pete is committed to bridging the gap between academia and industry, ensuring that individuals have access to the skills and knowledge they need to succeed in today's rapidly evolving world."
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/75f9c580-ff6c-4545-9839-76314bebae9f.png"
                    imageAlt="Pete Dumont"
                  >
                    <Avatar className="h-36 w-36 cursor-pointer">
                      <AvatarImage
                        src="/lovable-uploads/75f9c580-ff6c-4545-9839-76314bebae9f.png"
                        alt="Pete Dumont"
                      />
                      <AvatarFallback>PD</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-white text-black hover:bg-gray-100"
                        >
                          BIO
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-96">
                        <div className="space-y-2">
                          <h4 className="text-lg font-semibold">Pete Dumont</h4>
                          <p className="text-sm text-gray-600">{user.bio}</p>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white text-black hover:bg-gray-100"
                    >
                      NW
                    </Button>
                  </div>
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">Industries</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map((industry) => {
                        const Icon = industry.icon;
                        return (
                          <div
                            key={industry.name}
                            className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg"
                          >
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-serif text-3xl font-bold text-black">{user.name}</h2>
                    <a href={`mailto:${user.email}`} className="inline-flex items-center text-blue-600 hover:text-blue-800">
                      <Mail size={18} className="mr-1" />
                      <span className="text-sm">{user.email}</span>
                    </a>
                    <a href="/ispai-channel" className="ml-2">
                      <img 
                        src="/lovable-uploads/d7afdb11-1c93-4596-9b11-91cb70981610.png" 
                        alt="iSPAI Channel" 
                        className="w-16 h-16 hover:scale-110 transition-transform"
                      />
                    </a>
                  </div>
                  <p className="text-lg font-medium text-black mb-4">{user.role}</p>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Solution Bounties</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link
                          to="/solution-bounties/pete"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          View Details →
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={9.3} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Confirmed Solutions</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Knowledge Bases</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Domains</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map((domain) => (
                  <Badge
                    key={domain}
                    variant="outline"
                    className="border-2 border-black text-black"
                  >
                    {domain}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PeteProfile;
